/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES DEUX RÈGLES D'ORIGINE DE CETTE SURFACE, PURES ET ÉPROUVÉES.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Deux ressources de ce site viennent de l'extérieur, et les deux se décident par une
 * comparaison d'ORIGINE** : le **logo** d'une structure cliente, et le **cadre** du
 * formulaire de contact. Les deux règles vivent ici, ensemble, parce qu'un seul contrôle les
 * exerce et parce qu'elles se lisent l'une contre l'autre — **le cadre est la plus puissante
 * des deux ressources, et c'était la moins gardée.**
 *
 * `design-system-first` : « une image de contenu utilisateur ne se sert **jamais** depuis le
 * domaine de l'application, ni recopiée dans le dépôt, **ni proxifiée pour simplifier** —
 * proxifier ramène le fichier dans l'origine de l'application, ce que la règle existe pour
 * empêcher. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI CE FICHIER EXISTE À PART DU COMPOSANT QUI L'APPLIQUE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `web/src/shared/ui/ImageDeContenu.tsx` porte la même règle, et il porte aussi cette
 * phrase : « **exportée pour être testable sans navigateur** : c'est la seule règle de ce
 * fichier, et **une règle de sécurité qu'aucun test n'exerce est une règle qu'on croit
 * tenir**. »
 *
 * **CETTE PHRASE ÉTAIT UNE PROMESSE VIDE, ET C'EST LE CONSTAT QUI A FAIT NAÎTRE CE
 * FICHIER.** Aucun front de ce dépôt n'a de lanceur de tests — ni `web/`, ni `site/`, ni
 * `admin/` (D-090). La fonction était donc exportée **pour un test qui ne pouvait pas
 * exister**, et le seul effet de l'export était de le faire croire. Même famille que
 * D-088 : « une intention écrite en prose et une condition écrite en code peuvent différer
 * sans que rien ne le signale ».
 *
 * **Et la règle avait besoin d'être éprouvée plutôt que crue, D-092 vient de le montrer** :
 * en recette, cette comparaison **refuse chaque image de contenu de `web/`**, parce que la
 * variable nomme un domaine que rien ne sert. Le défaut n'est pas dans la fonction — elle
 * fait exactement ce qu'on lui demande — mais **c'est une fonction dont un faux positif
 * casse une surface entière en silence**, et c'est la définition d'un code qu'on veut
 * exercer.
 *
 * **CE QUI EST ICI EST DONC PUR : aucun React, aucun `import.meta`.** Le domaine se passe en
 * argument, et c'est ce qui rend le fichier exécutable par Node en dehors de tout
 * navigateur — `site/scripts/verifier-les-origines.mjs` les exerce **avant chaque
 * construction**, dans l'image comme en local. Ce n'est plus une vérification jouée une fois,
 * c'est un contrôle.
 *
 * **La lecture de la variable reste dans le composant**, où elle a un sens : une constante de
 * compilation dans un module pur en ferait un module impur, et le script ne pourrait plus le
 * charger.
 */

/**
 * **Vrai quand cette adresse vient du domaine du contenu.**
 *
 * La comparaison porte sur `origin` — schéma, hôte et port — et non sur le début de la
 * chaîne : `https://contenu.swype.fr.attaquant.test/x` commence par le bon domaine et n'en
 * est pas. **C'est le défaut classique de cette comparaison**, et c'est ce que `new URL()`
 * ferme.
 *
 * **Le sens du défaut est le refus.** Un domaine absent, vide ou illisible, une adresse
 * illisible : la fonction rend `false`, donc l'image ne s'affiche pas. Un composant qui
 * afficherait « en cas de doute » servirait un fichier déposé par quelqu'un depuis l'origine
 * de l'application, **ce que la règle existe précisément pour empêcher**.
 */
export function vientDuDomaineDeContenu(
  adresse: string,
  domaine: string | undefined,
): boolean {
  if (domaine === undefined || domaine === '') return false;
  try {
    return new URL(adresse).origin === new URL(domaine).origin;
  } catch {
    // Une adresse illisible n'est pas du bon domaine : le sens sûr des deux.
    return false;
  }
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE CADRE DOIT VENIR D'UNE AUTRE ORIGINE, SINON SON BAC À SABLE NE VAUT RIEN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'est le défaut le plus discret de la page de contact, et il n'était pas gardé.** Le
 * cadre porte `sandbox="allow-scripts allow-forms allow-same-origin"`, et **ces deux-là
 * ensemble annulent le bac à sable dès que le document encadré est de la MÊME origine que la
 * page** : le cadre atteint alors `parent.document`, donc il lit le document du site.
 *
 * Le commentaire du cadre affirmait que « `allow-same-origin` ne lui donne rien de CE site —
 * l'origine dont il parle est la sienne ». **C'est vrai uniquement d'une adresse
 * cross-origin, et rien ne l'imposait** : même famille que D-088, « une intention écrite en
 * prose et une condition écrite en code peuvent différer sans que rien ne le signale ».
 *
 * **LE SCÉNARIO N'EST PAS THÉORIQUE, ET C'EST CE QUI DÉCIDE D'AJOUTER CETTE GARDE.** Le
 * prérequis 21 se fermera en posant une valeur dans `VITE_CONTACT_IFRAME_URL`. Le réflexe
 * commode est de mettre un mandataire devant la plateforme, ou de servir la page sous
 * l'origine du site — **exactement le « proxifier pour simplifier » que la règle des images
 * de contenu interdit nommément**. Et `frame-src` étant dérivé de la même variable, la
 * politique de sécurité de contenu ne refuserait rien.
 *
 * **L'asymétrie rendait le manque visible** : le front contrôle l'origine des **logos**, et
 * ne contrôlait pas celle du **cadre** — qui est la ressource la plus puissante des deux.
 *
 * **Le sens du défaut est le refus** : une adresse illisible, relative, ou de la même origine
 * que la page **ne monte pas le cadre**. La page dit alors ce qui manque et met en avant
 * l'adresse email, comme elle le fait déjà quand le prérequis 21 est ouvert.
 */
export function leCadreVientDUneAutreOrigine(
  adresse: string | undefined,
  origineDeLaPage: string,
): boolean {
  if (adresse === undefined || adresse === '') return false;
  try {
    return new URL(adresse).origin !== new URL(origineDeLaPage).origin;
  } catch {
    // Une adresse illisible — relative, vide de schéma — n'est pas cross-origin : le
    // navigateur la résoudrait contre l'origine de la page, donc elle en serait.
    return false;
  }
}
